import React, { useState } from 'react';
import {
    SelectedFileBox,
    ThumbnailBody,
    ThumbnailFileName,
    ThumbnailImage,
    ThumbnailImageContainer,
    ThumbnailWrapper
} from './HBFileThumbnail.styles';
import { getFileIcon } from '../HBFileThumbnail/utils/getFileIcon';
import HBFileThumbnailActions from '../HBFileThumbnail/HBFileThumbnailActions';
import { IAttachmentFile } from '../HBFileThumbnail/HBFilesThumbnails';
import HBFilePreview from '../HBFilePreview/HBFilePreview';

export interface Props {
    /** Attachment file data object containing name, URL, type, and upload state. */
    file: IAttachmentFile;
    /** Shows delete and download action buttons below the thumbnail when true. */
    isDeletable: boolean;
    /** Enables selection mode where clicking toggles selection instead of opening a preview. */
    isSelectable: boolean;
    /** True when this file is currently in the selected set; shows the selection badge and highlight border. */
    isSelected: boolean;
    /** Called when the user clicks the delete button; receives the file object. Required when `isDeletable` is true. */
    onDeleteFile?: (file: IAttachmentFile) => void;
    /** Called when the user clicks the download button; receives the file object. Required when `isDeletable` is true. */
    onDownloadFile?: (file: IAttachmentFile) => void;
    /** Called when the thumbnail is clicked in selection mode; receives the file object. */
    onClickThumbnail?: (file: IAttachmentFile) => void;
    /** 1-based position number displayed inside the selection indicator badge when the file is selected. */
    selectedPosition?: number;
    /** Custom tooltip text shown on the delete action button. */
    deleteTooltipText?: string;
}

const HBFileThumbnail = ({
    file,
    isDeletable,
    onDeleteFile,
    onDownloadFile,
    isSelectable,
    isSelected,
    onClickThumbnail,
    selectedPosition,
    deleteTooltipText
}: Props) => {
    const fileIcon = getFileIcon(file);
    const [isPreviewShown, setIsPreviewShown] = useState(false);

    const _onClick = () => {
        if (isSelectable) {
            onClickThumbnail?.(file);
        } else {
            setIsPreviewShown(true);
        }
    };

    return (
        <ThumbnailWrapper
            onClick={_onClick}
            isUploading={file.isUploading}
            isSelected={isSelected}
            isSelectable={isSelectable}
        >
            {isSelectable && (
                <SelectedFileBox aria-hidden="true" visible={isSelected}>
                    {selectedPosition}
                </SelectedFileBox>
            )}

            <ThumbnailImageContainer>
                <ThumbnailImage src={fileIcon} />
            </ThumbnailImageContainer>

            <ThumbnailBody>
                <ThumbnailFileName type="emphasis12" color="text.primary">
                    {file.name}
                </ThumbnailFileName>
            </ThumbnailBody>

            {!isSelectable && (
                <HBFileThumbnailActions
                    onDeleteFile={onDeleteFile}
                    onDownloadFile={onDownloadFile}
                    isDeletable={isDeletable}
                    file={file}
                    deleteTooltipText={deleteTooltipText}
                ></HBFileThumbnailActions>
            )}

            {isPreviewShown && (
                <HBFilePreview
                    fileName={file.name}
                    fileUrl={file.url}
                    fileType="file"
                    onClose={() => setIsPreviewShown(false)}
                    onDownload={() => onDownloadFile(file)}
                    onDelete={() => onDeleteFile(file)}
                />
            )}
        </ThumbnailWrapper>
    );
};

export default HBFileThumbnail;
